// The console gate's refusal, as app state (spec/07 §6.6).
//
// Every console route answers `403 second-factor-required` to a session that has a first factor
// only — a phone code, a password on an account without an authenticator — when the member's role
// needs a second one. Whichever request meets it first raises this flag; the shell then shows the
// step-up screen in place of the page, and clears the flag once the session has proved the factor.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface SecondFactorGate {
  readonly required: boolean;
  readonly require: () => void;
  readonly satisfied: () => void;
}

/** How something outside React — the query cache — raises the flag. */
export interface SecondFactorSignal {
  readonly raise: () => void;
  readonly subscribe: (listener: () => void) => () => void;
}

export function createSecondFactorSignal(): SecondFactorSignal {
  const listeners = new Set<() => void>();
  return {
    raise: () => listeners.forEach((listener) => listener()),
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

const GateContext = createContext<SecondFactorGate | null>(null);

export function SecondFactorProvider({ signal, children }: { readonly signal: SecondFactorSignal; readonly children: ReactNode }) {
  const [required, setRequired] = useState(false);
  useEffect(() => signal.subscribe(() => setRequired(true)), [signal]);
  const gate = useMemo<SecondFactorGate>(
    () => ({ required, require: signal.raise, satisfied: () => setRequired(false) }),
    [required, signal],
  );
  return <GateContext.Provider value={gate}>{children}</GateContext.Provider>;
}

export function useSecondFactorGate(): SecondFactorGate {
  const gate = useContext(GateContext);
  if (gate === null) throw new Error('useSecondFactorGate() needs a <SecondFactorProvider> above it.');
  return gate;
}
